/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useMutation } from '@apollo/client';
import { Box, Button, useToast, Flex } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import moment from 'moment';
import 'moment/locale/en-gb';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import c from '../../constant';
import * as Yup from 'yup';
import useStore from '../../store';
import DropDownField from '../../components/form/DropDownField';
import DateTimeFormField from '../../utils/DateTimeFormField';
import LoadingOverlayText from '../../components/LoadingOverlay/LoadingOverlayText';

const MUTATION_UPDATE_EMPLOYMENT_STATUS = gql`
	mutation updateEmploymentStatus($input: EmploymentStatusInput!) {
		updateEmploymentStatus(input: $input)
	}
`;

const validationSchema = Yup.object().shape({
	employmentStatus: Yup.string().required('required'),
	employmentStatusDate: Yup.date().nullable().typeError('Please select a valid date').required('required'),
});

function UpdateStatusModal({ employeeId, currentEmploymentStatus, currentEmploymentStatusDate, onCloseModal, isEdit = false }) {
	moment.locale('en-gb');
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const toast = useToast();
	const { t } = useTranslation();
	const [updateEmploymentStatus, { loading }] = useMutation(MUTATION_UPDATE_EMPLOYMENT_STATUS);

	useEffect(() => window.scrollTo(0, 0), []);

	async function submit(values) {
		try {
			if (!employeeId) {
				throw new Error('EMPLOYEE_ID_NOT_FOUND');
			}

			const response = await updateEmploymentStatus({
				variables: {
					input: {
						employeeId,
						employmentStatus: values?.employmentStatus,
						employmentStatusDate: values?.employmentStatusDate,
					},
				},
			});

			const updated = response?.data?.updateEmploymentStatus;

			if (!updated) {
				throw new Error('EMPLOYMENT_STATUS_NOT_UPDATED');
			}

			toast({
				title: t('Employment status updated successfully'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
				duration: 5000,
			});

			if (onCloseModal) {
				onCloseModal();
			}
		} catch (error) {
			toast({
				title: t(error?.message ? error?.message : 'something went wrong'),
				status: 'error',
				duration: 5000,
				isClosable: true,
				position: 'bottom-right',
			});
		}
	}

	const initialValues = {
		employmentStatus: currentEmploymentStatus || '',
		employmentStatusDate: currentEmploymentStatusDate ? new Date(currentEmploymentStatusDate) : new Date(),
	};

	return (
		<>
			<Box w="100%" bg="white" borderRadius={c.borderRadius} ml="1px" pb="32px">
				<Box maxW="510px">
					<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={submit}>
						{(props) => {
							const { values } = props;
							return (
								<Box>
									<Form>
										<DropDownField
											{...props}
											label={t('Employment Status')}
											name="employmentStatus"
											placeholder={lang === 'en' ? 'Please select' : 'សូមជ្រើសរើស'}
											options={[
												{
													value: 'ACTIVE',
													label: 'Active',
													labelKh: 'សកម្ម',
												},
												{
													value: 'RESIGNED',
													label: 'Resigned',
													labelKh: 'បានលាលែងពីការងារ',
												},
											]}
										/>

										{values?.employmentStatus && (
											<DateTimeFormField
												{...props}
												label={values.employmentStatus === 'RESIGNED' ? t('Date of Resignation') : t('Date of Employment')}
												name="employmentStatusDate"
												value={values?.employmentStatusDate}
												years={true}
												nextTwoYear={false}
												required
											/>
										)}

										<Flex justify="end" mt={2}>
											<Button
												id="login-btn"
												variant="solid"
												size="md"
												w="128px"
												type="submit"
												borderRadius={c.borderRadius}
												borderColor="red.400"
												isLoading={loading}
												_focus={{ boxShadow: 'none' }}
											>
												{t('Save')}
											</Button>
										</Flex>
									</Form>
								</Box>
							);
						}}
					</Formik>
				</Box>
			</Box>

			{loading && <LoadingOverlayText />}
		</>
	);
}

export default UpdateStatusModal;
